import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const BrandLink = () => (
    <Link to="/" className="flex items-center gap-2 no-underline">
      {isHome ? (
        <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 m-0">
          Sugargoo <span className="text-primary-500">Spreadsheet</span>
        </h1>
      ) : (
        <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
          Sugargoo <span className="text-primary-500">Spreadsheet</span>
        </span>
      )}
    </Link>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <BrandLink />

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors no-underline whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors no-underline"
                    onClick={() => setIsOpen(false)}
                  >
                    <i className={`${cat.icon} text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/sugargoo-spreadsheet-guide"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors no-underline whitespace-nowrap"
          >
            <i className="ri-book-open-line"></i>
            Shopping Guide
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            Buy Now
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <button
            onClick={() => setMobileOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-sm h-full bg-background-50 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/50">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                Menu
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-4 py-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors no-underline mb-1"
              >
                <i className="ri-home-line text-primary-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium text-sm">Home</span>
              </Link>
              <Link
                to="/sugargoo-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors no-underline mb-1"
              >
                <i className="ri-book-open-line text-primary-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                <span className="font-medium text-sm">Shopping Guide</span>
              </Link>
            </div>

            <div className="px-5 py-2">
              <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Product Categories</h3>
            </div>

            <div className="px-4 pb-6">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors no-underline"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 flex-shrink-0">
                    <i className={`${cat.icon} text-foreground-600 text-base`}></i>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                    <span className="text-xs text-foreground-500">
                      {cat.name === 'Shoes' ? 'Sneakers & footwear' :
                       cat.name === 'Hoodies / Sweaters' ? 'Streetwear essentials' :
                       cat.name === 'Jackets' ? 'Outerwear & coats' :
                       cat.name === 'Bags' ? 'Backpacks & totes' :
                       cat.name === 'Accessories' ? 'Watches & jewelry' :
                       cat.name === 'Jersey' ? 'Sports & team wear' :
                       cat.name === 'Perfumes' ? 'Fragrances & scents' :
                       'Curated products'}
                    </span>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 ml-auto"></i>
                </a>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-background-200/50">
              <p className="text-xs text-foreground-500">
                This website provides educational information about Sugargoo Spreadsheets.
              </p>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}